import { userAuthHeader } from '../apiClient'
import * as errors from '../errors'
import type { IServiceError } from '../errors'
import {
  newRequestId,
  newIdempotencyKey,
  fetchOrThrowNetworkError,
  readErrorBody,
  parseErrorResponse,
  bodyMentions,
  IDEMPOTENCY_KEY_BODY_MISMATCH,
} from './utils'
import {
  pollOperation,
  saveInFlightOperation,
  removeInFlightOperation,
} from './asyncOperation'
import type { IEntityDataItem } from '~/types/models'
import type { IAsyncOptions } from '~/types/asyncOperation/models'
import { getPublicRuntimeConfig } from '~/cynqueModules/nuxtAccessors'
import { HTTPStatus } from '~/api/httpStatus'

// interface IPatchResponse {
//   version: Partial<IEntityDataItem> | Partial<IEntityDataItem>[]
// }

type TPatchResponse = Partial<IEntityDataItem> | Partial<IEntityDataItem>[]

export async function httpPatch<T>({
  callingMn,
  apiPath,
  entityData,
  asyncOptions,
}: {
  callingMn: string
  apiPath: string
  entityData: T
  asyncOptions?: IAsyncOptions
}): Promise<Awaited<TPatchResponse>> {
  const fn = `${callingMn} --> httpPatch |`
  if (!apiPath) {
    throw new Error(`${fn} called with undefined apiPath`)
  }
  const url = `${apiPath}`
  console.debug(`${fn} Sending request PATCH ${url}.`)

  const idempotencyKey = asyncOptions?.doAsync ? newIdempotencyKey() : undefined
  const headers: Record<string, string> = Object.assign(
    {
      'x-request-id': newRequestId(),
      'content-type': 'application/json-patch+json',
    },
    userAuthHeader()
  )
  if (idempotencyKey) {
    headers['Idempotency-Key'] = idempotencyKey
  }
  console.debug(`${fn} Request Id: ${headers['x-request-id']}`)
  if (idempotencyKey) {
    console.debug(`${fn} Idempotency-Key: ${idempotencyKey}`)
  }

  // version
  headers['x-app-version'] = getPublicRuntimeConfig().version

  // A transport failure (offline, DNS, reset, CORS, dyno restart) rejects here
  // as a typed NetworkError; HTTP status codes > 300 are NOT network errors and
  // still arrive as a resolved response for the switch below.
  const response = await fetchOrThrowNetworkError(url, {
    method: 'PATCH',
    headers,
    body: JSON.stringify(entityData),
  })
  switch (response.status) {
    case HTTPStatus.OK:
      const responseBody = await response.json()
      console.debug(`${fn} PATCH responseBody:`, responseBody)
      console.debug(
        `${fn} Returning PATCH responseBody.data:`,
        responseBody.data
      )
      return responseBody.data
    case HTTPStatus.Accepted: {
      const acceptedBody = await response.json()
      const operationId = acceptedBody.data.operation_id
      console.debug(`${fn} Async operation accepted: ${operationId}`)
      saveInFlightOperation(operationId, apiPath)
      try {
        const result = await pollOperation(
          operationId,
          callingMn,
          asyncOptions?.onPending
        )
        removeInFlightOperation(operationId)
        return result
      } catch (e) {
        removeInFlightOperation(operationId)
        throw e
      }
    }
    case HTTPStatus.BadRequest:
    case HTTPStatus.UnprocessableEntity: {
      const { message, body } = await parseErrorResponse(response)
      if (
        asyncOptions?.doAsync &&
        bodyMentions(body, IDEMPOTENCY_KEY_BODY_MISMATCH)
      ) {
        console.warn(
          `${fn} Idempotency-Key body mismatch — retrying with fresh key`
        )
        return httpPatch({ callingMn, apiPath, entityData, asyncOptions })
      }
      console.log(`${fn} rejected (${response.status}): ${message}`, body)
      throw new errors.ValidationError(
        response.status,
        'PATCH',
        url,
        message,
        body
      )
    }
    // Optimistic concurrency: the version we submitted is not the stored one.
    // `patchEntityData` can recover from this by refetching and retrying once;
    // see its `onConflictRefetch`.
    case HTTPStatus.Conflict:
      throw new errors.UpdateConflictError(
        'PATCH',
        url,
        await readErrorBody(response)
      )
    default: {
      // Was a bare `await response.json()`, which REJECTS on an empty or
      // non-JSON body and would replace the real failure with a SyntaxError.
      const { message, body } = await parseErrorResponse(response)
      throw new errors.APIUnhandledStatus(response.status, {
        statusCode: response.status,
        message,
        body,
      } as unknown as IServiceError)
    }
  }
}

export async function httpPatchMerge<T>({
  callingMn,
  apiPath,
  entityData,
  asyncOptions,
}: {
  callingMn: string
  apiPath: string
  entityData: T
  asyncOptions?: IAsyncOptions
}): Promise<Awaited<TPatchResponse>> {
  const fn = `${callingMn} --> httpPatchMerge |`
  if (!apiPath) {
    throw new Error(`${fn} called with undefined apiPath`)
  }
  const url = `${apiPath}`
  console.debug(`${fn} Sending request PATCH ${url}.`)

  const idempotencyKey = asyncOptions?.doAsync ? newIdempotencyKey() : undefined
  const headers: Record<string, string> = Object.assign(
    {
      'x-request-id': newRequestId(),
      'content-type': 'application/merge-patch+json',
    },
    userAuthHeader()
  )
  if (idempotencyKey) {
    headers['Idempotency-Key'] = idempotencyKey
  }
  console.debug(`${fn} Request Id: ${headers['x-request-id']}`)
  if (idempotencyKey) {
    console.debug(`${fn} Idempotency-Key: ${idempotencyKey}`)
  }

  // version
  headers['x-app-version'] = getPublicRuntimeConfig().version

  // A transport failure (offline, DNS, reset, CORS, dyno restart) rejects here
  // as a typed NetworkError; HTTP status codes > 300 are NOT network errors and
  // still arrive as a resolved response for the switch below.
  const response = await fetchOrThrowNetworkError(url, {
    method: 'PATCH',
    headers,
    body: JSON.stringify(entityData),
  })
  switch (response.status) {
    case HTTPStatus.OK:
      const responseBody = await response.json()
      console.debug(`${fn} PATCH responseBody:`, responseBody)
      console.debug(
        `${fn} Returning PATCH responseBody.data:`,
        responseBody.data
      )
      return responseBody.data
    case HTTPStatus.Accepted: {
      const acceptedBody = await response.json()
      const operationId = acceptedBody.data.operation_id
      console.debug(`${fn} Async operation accepted: ${operationId}`)
      saveInFlightOperation(operationId, apiPath)
      try {
        const result = await pollOperation(
          operationId,
          callingMn,
          asyncOptions?.onPending
        )
        removeInFlightOperation(operationId)
        return result
      } catch (e) {
        removeInFlightOperation(operationId)
        throw e
      }
    }
    case HTTPStatus.BadRequest:
    case HTTPStatus.UnprocessableEntity: {
      const { message, body } = await parseErrorResponse(response)
      if (
        asyncOptions?.doAsync &&
        bodyMentions(body, IDEMPOTENCY_KEY_BODY_MISMATCH)
      ) {
        console.warn(
          `${fn} Idempotency-Key body mismatch — retrying with fresh key`
        )
        return httpPatchMerge({ callingMn, apiPath, entityData, asyncOptions })
      }
      console.log(`${fn} rejected (${response.status}): ${message}`, body)
      throw new errors.ValidationError(
        response.status,
        'PATCH',
        url,
        message,
        body
      )
    }
    // Optimistic concurrency — see the matching case in httpPatch above.
    case HTTPStatus.Conflict:
      throw new errors.UpdateConflictError(
        'PATCH',
        url,
        await readErrorBody(response)
      )
    default: {
      // Was a bare `await response.json()`, which REJECTS on an empty or
      // non-JSON body and would replace the real failure with a SyntaxError.
      const { message, body } = await parseErrorResponse(response)
      throw new errors.APIUnhandledStatus(response.status, {
        statusCode: response.status,
        message,
        body,
      } as unknown as IServiceError)
    }
  }
}
